import { OutlineFilter } from '@pixi/filter-outline'
import * as PIXI from 'pixi.js'
import '@pixi/extract'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePlantInteractions } from '../hooks/usePlantInteractions'
import { drawPlant } from '../pixi/plantRenderer'
import { createFloatingTextParticle } from '../pixi/textParticles'
import { useGameStore } from '../store/gameStore'
import { formatDuration } from '../utils/time'
import MultiPlantToolbar from './MultiPlantToolbar'
import OffscreenIndicators from './OffscreenIndicators'
import PendingSeedsOverlay from './PendingSeedsOverlay'
import ToolHintOverlay from './ToolHintOverlay'
import WaterNotification from './WaterNotification'
import { useI18n } from '../i18n/I18nProvider'

const BASE_WIDTH = 800
const BASE_HEIGHT = 600

function MultiPlantCanvas({
  pendingSeedType,
  onSeedPlaced,
  onCancelSeed,
  onRequestPublish,
  autoGardenerOwned = false,
  autoGardenerLastTick = null,
  autoGardenerConfig = null,
}) {
  const canvasRef = useRef(null)
  const appRef = useRef(null)
  const particlesContainerRef = useRef(null)
  const pendingSeedTypeRef = useRef(pendingSeedType)
  const selectedModeRef = useRef('sun')
  const isAnimatingRef = useRef(false)
  const lastPixelsRef = useRef({}) // Store last pixel for each plant by ID
  const previousProgressRef = useRef({}) // Store previous progress for each plant
  const plantContainersRef = useRef(new Map())
  const plantPoolRef = useRef([])
  const plantPositionsRef = useRef(new Map())
  const lastClickFeedbackRef = useRef({})
  const lastSnapshotRef = useRef(null)

  const plants = useGameStore((state) => state.plants)
  const config = useGameStore((state) => state.config)
  const selectedBackground = useGameStore((state) => state.selectedBackground)
  const waterLevel = useGameStore((state) => state.waterLevel)
  const addProgressToPlant = useGameStore((state) => state.addProgressToPlant)
  const waterPlant = useGameStore((state) => state.waterPlant)
  const collectSeedFromPlant = useGameStore((state) => state.collectSeedFromPlant)
  const destroyPlantById = useGameStore((state) => state.destroyPlantById)
  const prunePlantBranches = useGameStore((state) => state.prunePlantBranches)
  const getWaterCooldownRemaining = useGameStore((state) => state.getWaterCooldownRemaining)
  const ownedTools = useGameStore((state) => state.ownedTools)
  const { t } = useI18n()
  
  const [pendingSeeds, setPendingSeeds] = useState([]) // { position: {x,y}, type, graphics }
  const [isAnimating, setIsAnimating] = useState(false)
  const [animatingSeed, setAnimatingSeed] = useState(null)
  const [selectedMode, setSelectedMode] = useState('sun')
  const [waterNotification, setWaterNotification] = useState(null)
  const [offscreenLeft, setOffscreenLeft] = useState(false)
  const [offscreenRight, setOffscreenRight] = useState(false)
  const [activeToolHint, setActiveToolHint] = useState(null)
  const [gardenerPulse, setGardenerPulse] = useState(false)
  const [waterCooldownMs, setWaterCooldownMs] = useState(0)
  const scaleRef = useRef(1)
  const viewScaleRef = useRef(1)
  const viewOffsetRef = useRef({ x: 0, y: 0 })
  const isPanningRef = useRef(false)
  const lastPointerPosRef = useRef(null)
  const initialOffsetRef = useRef({ x: 0, y: 0 })
  const pinchStateRef = useRef(null) // { startDistance, startScale }
  const containerSizeRef = useRef({ width: BASE_WIDTH, height: BASE_HEIGHT })
  const hasAutoFocusedRef = useRef(false)
  const updateStageTransformRef = useRef(null)
  const gardenerTickRef = useRef(autoGardenerLastTick)
  const gardenerPulseTimeoutRef = useRef(null)
  
  const hasPlants = useMemo(() => Array.isArray(plants) && plants.length > 0, [plants])
  const hasMaturePlant = useMemo(() => {
    if (!hasPlants) return false
    return plants.some((plant) => {
      if (!plant?.config) return false
      const clicksPerStage = plant.config.clicksPerStage || 0
      const stages = plant.config.stages || 0
      const totalClicks = clicksPerStage * stages
      if (totalClicks <= 0) return false
      return (plant.progress || 0) >= totalClicks
    })
  }, [hasPlants, plants])

  // Update refs
  useEffect(() => {
    pendingSeedTypeRef.current = pendingSeedType
  }, [pendingSeedType])
  
  useEffect(() => {
    selectedModeRef.current = selectedMode
  }, [selectedMode])
  
  useEffect(() => {
    isAnimatingRef.current = isAnimating
  }, [isAnimating])

  useEffect(() => {
    if (!autoGardenerOwned) {
      setGardenerPulse(false)
      if (gardenerPulseTimeoutRef.current) {
        clearTimeout(gardenerPulseTimeoutRef.current)
        gardenerPulseTimeoutRef.current = null
      }
      gardenerTickRef.current = autoGardenerLastTick
      return
    }

    if (!autoGardenerLastTick) return
    if (gardenerTickRef.current === autoGardenerLastTick) return

    gardenerTickRef.current = autoGardenerLastTick
    setGardenerPulse(true)

    if (gardenerPulseTimeoutRef.current) {
      clearTimeout(gardenerPulseTimeoutRef.current)
    }

    gardenerPulseTimeoutRef.current = setTimeout(() => {
      setGardenerPulse(false)
      gardenerPulseTimeoutRef.current = null
    }, 1500)
  }, [autoGardenerOwned, autoGardenerLastTick])

  useEffect(() => {
    return () => {
      if (gardenerPulseTimeoutRef.current) {
        clearTimeout(gardenerPulseTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const updateRemaining = () => {
      const remaining = getWaterCooldownRemaining()
      setWaterCooldownMs(remaining)
    }

    updateRemaining()
    const interval = setInterval(updateRemaining, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [getWaterCooldownRemaining])

  const triggerAutoClickFeedback = useCallback((plantId, clicks = 1) => {
    const app = appRef.current
    if (!app) return

    const now = performance.now()
    const lastClick = lastClickFeedbackRef.current[plantId] || 0
    if (now - lastClick < 50) {
      return
    }
    lastClickFeedbackRef.current[plantId] = now

    let plantContainer = plantContainersRef.current.get(plantId)
    if (plantContainer && plantContainer.destroyed) {
      plantContainersRef.current.delete(plantId)
      plantContainer = null
    }
    if (!plantContainer) {
      plantContainer = app.stage.children.find(
        (child) => child.name === 'plant' && child.userData && child.userData.plantId === plantId
      )
      if (plantContainer && !plantContainer.destroyed) {
        plantContainersRef.current.set(plantId, plantContainer)
      }
    }

    if (plantContainer) {
      plantContainer.userData = plantContainer.userData || {}
      if (!plantContainer.userData.baseScale) {
        plantContainer.userData.baseScale = {
          x: plantContainer.scale.x || 1,
          y: plantContainer.scale.y || 1,
        }
      }
      plantContainer.userData.clickBoost = Math.min(
        (plantContainer.userData.clickBoost || 0) + 0.6,
        1.2
      )

      if (!plantContainer.userData.clickOutline) {
        const outline = new OutlineFilter(3, 0xffffff)
        plantContainer.userData.clickOutline = outline
        const existing = plantContainer.filters || []
        plantContainer.filters = [...existing, outline]
      } else if (!(plantContainer.filters || []).includes(plantContainer.userData.clickOutline)) {
        plantContainer.filters = [...(plantContainer.filters || []), plantContainer.userData.clickOutline]
      }

      if (plantContainer.userData.clickOutlineTimeout) {
        clearTimeout(plantContainer.userData.clickOutlineTimeout)
      }
      plantContainer.userData.clickOutlineTimeout = setTimeout(() => {
        if (!plantContainer.destroyed && plantContainer.userData?.clickOutline) {
          plantContainer.filters = (plantContainer.filters || []).filter(
            (filter) => filter !== plantContainer.userData.clickOutline
          )
        }
        if (plantContainer.userData) {
          plantContainer.userData.clickOutlineTimeout = null
        }
      }, 400)
    }

    const targetPosition = plantPositionsRef.current.get(plantId)
    if (!targetPosition) return

    createFloatingTextParticle(
      app,
      targetPosition.x,
      targetPosition.y - 110,
      `+${clicks}`,
      {
        fontSize: 14,
        color: 0xfff1a1,
        dy: -0.6,
        alphaStep: 0.025,
        maxParticles: 80,
      },
    )
  }, [])

  useEffect(() => {
    if (!autoGardenerOwned) return

    const handleAutoClick = (event) => {
      const { plantId, clicks = 1 } = event?.detail || {}
      if (!plantId) return
      triggerAutoClickFeedback(plantId, clicks)
    }

    window.addEventListener('autoGardenerClick', handleAutoClick)

    return () => {
      window.removeEventListener('autoGardenerClick', handleAutoClick)
    }
  }, [autoGardenerOwned, triggerAutoClickFeedback])

  // Initialize canvas ONCE
  useEffect(() => {
    if (!canvasRef.current || !config || appRef.current) return

    const backgroundConfig = config.shop?.cosmetics?.find(item => item.id === selectedBackground)
    const gradient = backgroundConfig?.gradient || ['#87CEEB', '#90EE90']

    const containerRect = canvasRef.current.getBoundingClientRect()
    const containerWidth = containerRect.width || window.innerWidth
    const containerHeight = containerRect.height || window.innerHeight * 0.7

    // On considère que la hauteur du conteneur (~70vh) définit l'échelle visuelle de base
    containerSizeRef.current = { width: containerWidth, height: containerHeight }

    // L'échelle "de base" du monde Pixi reste 1, le remplissage visuel se fait via la hauteur CSS
    const scale = 1
    scaleRef.current = scale
    viewScaleRef.current = 1
    viewOffsetRef.current = { x: 0, y: 0 }

    const app = new PIXI.Application({
      width: BASE_WIDTH,
      height: BASE_HEIGHT,
      backgroundColor: 0x000000,
      backgroundAlpha: 0,
      antialias: false,
      resolution: window.devicePixelRatio || 1,
    })

    // La hauteur du canvas suit le conteneur, la largeur s'ajuste automatiquement (ratio 800x600)
    app.view.style.width = 'auto'
    app.view.style.height = '100%'
    app.view.style.display = 'block'
    app.view.style.margin = '0 auto'

    app.stage.scale.set(scale)
    app.stage.position.set(0, 0)

    appRef.current = app

    app.renderer.resize(BASE_WIDTH, BASE_HEIGHT)

    canvasRef.current.appendChild(app.view)
    appRef.current = app
    
    // Create gradient background
    const bgGraphics = new PIXI.Graphics()
    const topColor = parseInt(gradient[0].replace('#', ''), 16)
    const bottomColor = parseInt(gradient[1].replace('#', ''), 16)
    
    // Créer un dégradé vertical en dessinant plusieurs rectangles
    const steps = 100
    for (let i = 0; i < steps; i++) {
      const ratio = i / steps
      const r = Math.round(((topColor >> 16) & 0xFF) * (1 - ratio) + ((bottomColor >> 16) & 0xFF) * ratio)
      const g = Math.round(((topColor >> 8) & 0xFF) * (1 - ratio) + ((bottomColor >> 8) & 0xFF) * ratio)
      const b = Math.round((topColor & 0xFF) * (1 - ratio) + (bottomColor & 0xFF) * ratio)
      const color = (r << 16) | (g << 8) | b
      
      bgGraphics.beginFill(color)
      bgGraphics.drawRect(0, i * (BASE_HEIGHT / steps), BASE_WIDTH, BASE_HEIGHT / steps + 1)
      bgGraphics.endFill()
    }
    bgGraphics.name = 'background'
    app.stage.addChildAt(bgGraphics, 0)

    // Ambient clouds layer (derrière le sol)
    const cloudsContainer = new PIXI.Container()
    cloudsContainer.name = 'clouds'
    app.stage.addChild(cloudsContainer)

    // Ambient pollen layer (léger, derrière les plantes)
    const pollenContainer = new PIXI.Container()
    pollenContainer.name = 'pollen'
    app.stage.addChild(pollenContainer)

    // Draw ground line
    const groundLine = new PIXI.Graphics()
    groundLine.lineStyle(3, 0x8B4513, 1)
    groundLine.moveTo(0, BASE_HEIGHT - 150)
    groundLine.lineTo(BASE_WIDTH, BASE_HEIGHT - 150)
    groundLine.name = 'groundLine'
    app.stage.addChild(groundLine)

    // Particles container
    const particlesContainer = new PIXI.Container()
    particlesContainer.name = 'particles'
    app.stage.addChild(particlesContainer)
    particlesContainerRef.current = particlesContainer

    // Particules de gameplay + poussière ambiante
    const particles = []
    const ambientPollen = []
    const ambientClouds = []

    const spawnPollen = () => {
      const x = Math.random() * BASE_WIDTH
      const y = BASE_HEIGHT - 160 - Math.random() * 40
      const dot = new PIXI.Graphics()
      dot.beginFill(0xFDE68A, 0.85)
      dot.drawCircle(0, 0, 2)
      dot.endFill()
      dot.position.set(x, y)
      pollenContainer.addChild(dot)
      ambientPollen.push({
        graphics: dot,
        life: 0,
        vy: -0.15 - Math.random() * 0.15,
        vx: (Math.random() - 0.5) * 0.2,
      })
    }

    const createCloud = (x, y, scale = 1) => {
      const cloud = new PIXI.Graphics()
      cloud.beginFill(0xffffff, 0.9)
      cloud.drawEllipse(0, 0, 40, 20)
      cloud.drawEllipse(-25, -5, 25, 15)
      cloud.drawEllipse(25, -5, 25, 15)
      cloud.endFill()
      cloud.position.set(x, y)
      cloud.scale.set(scale)
      cloudsContainer.addChild(cloud)
      return { graphics: cloud, speed: 0.15 + Math.random() * 0.1 }
    }

    ambientClouds.push(createCloud(-100, 80, 0.9))
    ambientClouds.push(createCloud(200, 120, 1.05))
    ambientClouds.push(createCloud(500, 60, 0.85))

    // Ticker pour particules de gameplay (sun/water/text) et poussière ambiante
    let ambientTime = 0

    const particlesTicker = (delta) => {
      const dt = delta || 1
      ambientTime += dt

      // Gameplay particles déjà stockées dans app.userData?.particles
      const gameParticles = (app.userData && app.userData.particles) || []
      for (let i = gameParticles.length - 1; i >= 0; i--) {
        const particle = gameParticles[i]
        if (!particle || !particle.update || !particle.isDead) continue
        particle.update()
        if (particle.isDead()) {
          if (particle.destroy) {
            particle.destroy()
          } else if (particle.graphics && particle.graphics.parent) {
            particle.graphics.parent.removeChild(particle.graphics)
          }
          gameParticles.splice(i, 1)
        }
      }

      // Pollen ambiant : spawn léger et montée
      if (ambientPollen.length < 40 && Math.random() < 0.08) {
        spawnPollen()
      }

      for (let i = ambientPollen.length - 1; i >= 0; i--) {
        const p = ambientPollen[i]
        if (!p.graphics || !p.graphics.parent) {
          ambientPollen.splice(i, 1)
          continue
        }
        p.life += dt
        p.graphics.x += p.vx * dt
        p.graphics.y += p.vy * dt
        p.graphics.alpha = Math.max(0, 1 - p.life / 260)
        if (p.life > 260) {
          p.graphics.parent.removeChild(p.graphics)
          ambientPollen.splice(i, 1)
        }
      }

      // Déplacement horizontal très lent des nuages
      ambientClouds.forEach((cloudObj) => {
        const { graphics, speed } = cloudObj
        if (!graphics || !graphics.parent) return
        graphics.x += speed * dt
        if (graphics.x - 80 > BASE_WIDTH) {
          graphics.x = -120
        }
      })

      // Pulser légèrement la lumière du background (très subtil)
      const pulse = 0.03 * Math.sin(ambientTime / 180)
      bgGraphics.alpha = 1 + pulse
    }

    // Handle clicks/touches
    const getEffectiveScale = () => {
      const base = scaleRef.current || 1
      const view = viewScaleRef.current || 1
      return base * view
    }

    const clampOffset = (offset) => {
      const { width: containerW, height: containerH } = containerSizeRef.current
      const effectiveScale = getEffectiveScale()
      const worldWidth = BASE_WIDTH * effectiveScale
      const worldHeight = BASE_HEIGHT * effectiveScale

      // Empêcher le jardin de sortir complètement de l'écran
      const minX = Math.min(0, containerW - worldWidth)
      const maxX = 0
      const minY = Math.min(0, containerH - worldHeight)
      const maxY = 0

      return {
        x: Math.max(minX, Math.min(maxX, offset.x)),
        y: Math.max(minY, Math.min(maxY, offset.y)),
      }
    }

    const updateStageTransform = () => {
      const appInstance = appRef.current
      if (!appInstance) return
      const effectiveScale = getEffectiveScale()
      appInstance.stage.scale.set(effectiveScale)
      const clamped = clampOffset(viewOffsetRef.current)
      viewOffsetRef.current = clamped
      appInstance.stage.position.set(clamped.x, clamped.y)

      // Mettre à jour les bulles d'indication si des plantes sont hors écran
      const plantsState = useGameStore.getState().plants
      if (!plantsState || plantsState.length === 0) {
        setOffscreenLeft(false)
        setOffscreenRight(false)
        return
      }

      const { width: containerW } = containerSizeRef.current
      const viewLeft = -clamped.x / effectiveScale
      const viewRight = viewLeft + containerW / effectiveScale

      let minX = Infinity
      let maxX = -Infinity
      plantsState.forEach((p) => {
        if (!p || !p.position) return
        const x = p.position.x
        if (x < minX) minX = x
        if (x > maxX) maxX = x
      })

      setOffscreenLeft(minX < viewLeft - 5)
      setOffscreenRight(maxX > viewRight + 5)
    }

    // Pan (drag) - souris & touch simple
    const handlePointerDown = (e) => {
      const rect = app.view.getBoundingClientRect()
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX)
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY)
      if (!clientX || !clientY) return

      // Pinch: si deux doigts, initialiser le zoom
      if (e.touches && e.touches.length === 2) {
        const [t1, t2] = e.touches
        const dx = t2.clientX - t1.clientX
        const dy = t2.clientY - t1.clientY
        const distance = Math.sqrt(dx * dx + dy * dy) || 1
        pinchStateRef.current = {
          startDistance: distance,
          startScale: viewScaleRef.current || 1,
        }
        isPanningRef.current = false
        return
      }

      isPanningRef.current = true
      lastPointerPosRef.current = { x: clientX, y: clientY }
      initialOffsetRef.current = { ...viewOffsetRef.current }
    }

    const handlePointerMove = (e) => {
      // Pinch zoom
      if (e.touches && e.touches.length === 2 && pinchStateRef.current) {
        const [t1, t2] = e.touches
        const dx = t2.clientX - t1.clientX
        const dy = t2.clientY - t1.clientY
        const distance = Math.sqrt(dx * dx + dy * dy) || 1
        const { startDistance, startScale } = pinchStateRef.current
        let newViewScale = (startScale * distance) / startDistance
        const MIN_VIEW_SCALE = 0.7
        const MAX_VIEW_SCALE = 3
        newViewScale = Math.max(MIN_VIEW_SCALE, Math.min(MAX_VIEW_SCALE, newViewScale))
        viewScaleRef.current = newViewScale
        updateStageTransform()
        return
      }

      if (!isPanningRef.current || !lastPointerPosRef.current) return
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX)
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY)
      if (!clientX || !clientY) return

      const dx = clientX - lastPointerPosRef.current.x
      const dy = clientY - lastPointerPosRef.current.y

      const newOffset = {
        x: initialOffsetRef.current.x + dx,
        y: initialOffsetRef.current.y + dy,
      }

      viewOffsetRef.current = clampOffset(newOffset)
      updateStageTransform()
    }

    const handlePointerUp = () => {
      isPanningRef.current = false
      lastPointerPosRef.current = null
      pinchStateRef.current = null
    }

    // Préparer le stockage des particules de gameplay utilisées par les helpers
    app.userData = { particles }

    // Enregistrer le ticker de particules (poussière + gameplay)
    app.ticker.add(particlesTicker)

    app.view.addEventListener('click', handleInteraction)
    app.view.addEventListener('touchstart', handleInteraction)
    app.view.addEventListener('mousedown', handlePointerDown)
    app.view.addEventListener('mousemove', handlePointerMove)
    app.view.addEventListener('mouseup', handlePointerUp)
    app.view.addEventListener('mouseleave', handlePointerUp)
    app.view.addEventListener('touchstart', handlePointerDown)
    app.view.addEventListener('touchmove', handlePointerMove)
    app.view.addEventListener('touchend', handlePointerUp)
    app.view.addEventListener('touchcancel', handlePointerUp)

    return () => {
      app.view.removeEventListener('click', handleInteraction)
      app.view.removeEventListener('touchstart', handleInteraction)
      app.view.removeEventListener('mousedown', handlePointerDown)
      app.view.removeEventListener('mousemove', handlePointerMove)
      app.view.removeEventListener('mouseup', handlePointerUp)
      app.view.removeEventListener('mouseleave', handlePointerUp)
      app.view.removeEventListener('touchstart', handlePointerDown)
      app.view.removeEventListener('touchmove', handlePointerMove)
      app.view.removeEventListener('touchend', handlePointerUp)
      app.view.removeEventListener('touchcancel', handlePointerUp)

      // Nettoyer le ticker de particules
      app.ticker.remove(particlesTicker)
      app.destroy(true, { children: true, texture: true })
      appRef.current = null

      plantContainersRef.current = new Map()
      plantPoolRef.current = []
      plantPositionsRef.current = new Map()
      lastPixelsRef.current = {}
      previousProgressRef.current = {}
    }
  }, [config, selectedBackground]);

  // Render plants - only redraw when plants array changes
  useEffect(() => {
    if (!appRef.current || !config) return;
    
    const app = appRef.current;
    
    const containers = plantContainersRef.current
    const pool = plantPoolRef.current
    const currentPlantIds = new Set(plants.map((p) => p.id))

    // Remove containers for deleted plants
    containers.forEach((container, plantId) => {
      if (!currentPlantIds.has(plantId) || container.destroyed) {
        if (container.parent) {
          container.parent.removeChild(container)
        }
        if (container.userData?.clickOutlineTimeout) {
          clearTimeout(container.userData.clickOutlineTimeout)
        }
        container.removeChildren()
        container.filters = []
        container.userData = {}
        container.position.set(0, 0)
        container.scale.set(1, 1)
        container.rotation = 0
        container.alpha = 1
        pool.push(container)
        containers.delete(plantId)
        delete lastPixelsRef.current[plantId]
        delete previousProgressRef.current[plantId]
        plantPositionsRef.current.delete(plantId)
      }
    })

    // Draw plants
    if (plants && plants.length > 0) {
      plants.forEach((plant) => {
        if (!plant || !plant.position || !plant.config) return;
        let plantContainer = containers.get(plant.id)
        if (plantContainer && plantContainer.destroyed) {
          containers.delete(plant.id)
          plantContainer = null
        }

        if (!plantContainer) {
          do {
            plantContainer = plantPoolRef.current.pop()
          } while (plantContainer && plantContainer.destroyed)

          if (!plantContainer) {
            plantContainer = new PIXI.Container()
            plantContainer.name = 'plant'
          }
          plantContainer.userData = { plantId: plant.id }
          app.stage.addChild(plantContainer)
          containers.set(plant.id, plantContainer)
        } else {
          plantContainer.userData = { ...plantContainer.userData, plantId: plant.id }
        }

        plantContainer.position.set(plant.position.x, plant.position.y)
        plantPositionsRef.current.set(plant.id, { x: plant.position.x, y: plant.position.y })

        // Reset filters; transient outlines will be re-added when active
        plantContainer.filters = []
        plantContainer.scale.set(1, 1)
        plantContainer.rotation = 0
        plantContainer.alpha = 1
        plantContainer.removeChildren()

        drawPlant(plantContainer, plant, config, false, lastPixelsRef, plant.id);

        // Si la plante vient juste d'être créée (progress 0), dessiner une grosse graine bien visible au niveau du sol
        if ((plant.progress || 0) === 0) {
          const seedCircle = new PIXI.Graphics();
          seedCircle.beginFill(0x8B4513);
          seedCircle.lineStyle(2, 0xffffff, 1);
          seedCircle.drawCircle(0, 0, 10);
          seedCircle.endFill();
          plantContainer.addChild(seedCircle);
        }

        // Icône emoji 🌱 au-dessus des très jeunes plantes pour les rendre plus visibles
        const earlyClicksThreshold = 5;
        if ((plant.progress || 0) < earlyClicksThreshold) {
          const sproutText = new PIXI.Text('🌱', {
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: 26,
          });
          sproutText.anchor.set(0.5, 0.5);
          sproutText.position.set(0, -90);
          plantContainer.addChild(sproutText);
        }

        // Indicateur de graine prête à être collectée : outline + icône graine
        if (plant.pendingSeed) {
          // Outline net autour de la silhouette de la plante
          if (!plantContainer.userData.pendingSeedFilter) {
            plantContainer.userData.pendingSeedFilter = new OutlineFilter(2, 0xffffff)
          }
          const hasFilter = (plantContainer.filters || []).includes(plantContainer.userData.pendingSeedFilter)
          if (!hasFilter) {
            plantContainer.filters = [...(plantContainer.filters || []), plantContainer.userData.pendingSeedFilter]
          }

          // Petite graine au-dessus de la plante
          const seedIcon = new PIXI.Graphics();
          seedIcon.beginFill(0xF4D03F);
          seedIcon.drawCircle(0, -90, 10);
          seedIcon.endFill();
          plantContainer.addChild(seedIcon);
        } else if (plantContainer.userData?.pendingSeedFilter) {
          plantContainer.filters = (plantContainer.filters || []).filter(
            (filter) => filter !== plantContainer.userData.pendingSeedFilter
          )
        }
      });

      // Autofocus sur une fleur représentative au premier rendu
      if (!hasAutoFocusedRef.current) {
        const appInstance = appRef.current
        if (appInstance) {
          const representative = plants[Math.floor(plants.length / 2)] || plants[0]
          if (representative && representative.position) {
            const effectiveScale = (scaleRef.current || 1) * (viewScaleRef.current || 1)
            const { width: containerW } = containerSizeRef.current
            const targetWorldX = representative.position.x
            const desiredOffsetX = containerW / 2 - targetWorldX * effectiveScale

            viewOffsetRef.current = { x: desiredOffsetX, y: 0 }
            if (updateStageTransformRef.current) {
              updateStageTransformRef.current()
            }

            hasAutoFocusedRef.current = true
          }
        }
      }
    }
  }, [config, plants]);

  const handleValidatePendingSeeds = () => {
    if (!pendingSeeds.length || !onSeedPlaced) return;

    pendingSeeds.forEach(({ graphics }) => {
      if (graphics && graphics.parent) {
        graphics.parent.removeChild(graphics);
      }
    });

    pendingSeeds.forEach((seed) => {
      onSeedPlaced(seed.position, seed.type);
    });

    setPendingSeeds([]);

    if (onCancelSeed) {
      onCancelSeed();
    }

    // Recentrer la vue sur la dernière graine validée pour rendre la jeune plante visible
    const lastSeed = pendingSeeds[pendingSeeds.length - 1];
    if (lastSeed && updateStageTransformRef.current && appRef.current) {
      const appInstance = appRef.current;
      const effectiveScale = (scaleRef.current || 1) * (viewScaleRef.current || 1);
      const { width: containerW } = containerSizeRef.current;
      const targetWorldX = lastSeed.position.x;
      const desiredOffsetX = containerW / 2 - targetWorldX * effectiveScale;

      viewOffsetRef.current = { x: desiredOffsetX, y: 0 };
      updateStageTransformRef.current();
    }
  };

  const handleModeClick = (mode) => {
    if (!hasPlants) {
      setWaterNotification(t('multiGarden.waterNeedPlant'))
      setTimeout(() => {
        setWaterNotification(null)
      }, 4000)
      return
    }

    if (mode === 'water') {
      const remaining = getWaterCooldownRemaining()
      if (remaining > 0 || waterLevel < 10) {
        if (remaining > 0) {
          setWaterNotification(`${t('multiGarden.waterReadyPrefix')} ${formatDuration(remaining)}.`)
        } else if (waterLevel < 10) {
          setWaterNotification(t('multiGarden.waterNoWater'))
        }

        setTimeout(() => {
          setWaterNotification(null)
        }, 4000)

        return
      }
    }

    setSelectedMode(mode)
  }

  const TOOL_HINTS = useMemo(() => ({
    sun: t('multiGarden.toolHints.sun'),
    water: t('multiGarden.toolHints.water'),
    hand: t('multiGarden.toolHints.hand'),
    pruner: t('multiGarden.toolHints.pruner'),
    camera: t('multiGarden.toolHints.camera'),
    zoomIn: t('multiGarden.toolHints.zoomIn'),
    zoomOut: t('multiGarden.toolHints.zoomOut'),
  }), [t])

  const hintTimeoutRef = useRef(null)

  const { handleInteraction } = usePlantInteractions({
    appRef,
    pendingSeedTypeRef,
    selectedModeRef,
    isAnimatingRef,
    setIsAnimating,
    setPendingSeeds,
    particlesContainerRef,
    onCancelSeed,
  })

  const captureGardenSnapshot = useCallback(() => {
    const app = appRef.current
    if (!app) return null
    try {
      app.renderer.render(app.stage)
      let snapshot = null
      if (app.renderer?.view?.toDataURL) {
        snapshot = app.renderer.view.toDataURL('image/png')
      }
      if (!snapshot) {
        snapshot = app.renderer.plugins.extract.base64(app.stage)
      }
      lastSnapshotRef.current = snapshot
      return snapshot
    } catch (error) {
      console.error('Failed to capture garden snapshot:', error)
      return lastSnapshotRef.current
    }
  }, [])

  const handlePublishRequest = useCallback(() => {
    if (!onRequestPublish) return
    const snapshot = captureGardenSnapshot()
    onRequestPublish(snapshot)
  }, [captureGardenSnapshot, onRequestPublish])

  const showToolHint = (key) => {
    if (!key) return
    setActiveToolHint(TOOL_HINTS[key] || null)
  }

  const hideToolHint = () => {
    setActiveToolHint(null)
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current)
      hintTimeoutRef.current = null
    }
  }

  const handleToolTouchStart = (key) => {
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current)
    }
    // Appui long ~600ms pour afficher l'aide sur mobile
    hintTimeoutRef.current = setTimeout(() => {
      showToolHint(key)
    }, 600)
  }

  const handleToolTouchEnd = () => {
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current)
      hintTimeoutRef.current = null
    }
  }

  const handleZoomButton = (direction) => {
    const MIN_VIEW_SCALE = 0.7
    const MAX_VIEW_SCALE = 3
    const step = direction === 'in' ? 1.2 : 1 / 1.2

    let newViewScale = (viewScaleRef.current || 1) * step
    newViewScale = Math.max(MIN_VIEW_SCALE, Math.min(MAX_VIEW_SCALE, newViewScale))
    viewScaleRef.current = newViewScale

    const app = appRef.current
    if (!app) return

    const baseScale = scaleRef.current || 1
    const effectiveScale = baseScale * newViewScale
    app.stage.scale.set(effectiveScale)

    const { width: containerW, height: containerH } = containerSizeRef.current
    const worldWidth = BASE_WIDTH * effectiveScale
    const worldHeight = BASE_HEIGHT * effectiveScale

    const minX = Math.min(0, containerW - worldWidth)
    const maxX = 0
    const minY = Math.min(0, containerH - worldHeight)
    const maxY = 0

    const currentOffset = viewOffsetRef.current || { x: 0, y: 0 }
    const clampedOffset = {
      x: Math.max(minX, Math.min(maxX, currentOffset.x)),
      y: Math.max(minY, Math.min(maxY, currentOffset.y)),
    }

    viewOffsetRef.current = clampedOffset
    app.stage.position.set(clampedOffset.x, clampedOffset.y)
  }

  return (
    <div className="relative w-full max-w-3xl mx-auto h-[70vh] flex items-center justify-center bg-gradient-to-b from-sky-300 to-green-100 rounded-xl overflow-hidden shadow-2xl">
      {autoGardenerOwned && (
        <div
          className={`absolute top-4 left-4 flex items-center gap-3 rounded-2xl border border-white/30 bg-green-600/90 px-4 py-3 text-white shadow-xl transition-all ${
            gardenerPulse ? 'ring-4 ring-white/40 animate-pulse' : 'ring-2 ring-white/10'
          }`}
          aria-live="polite"
        >
          <span className="text-2xl" role="img" aria-label="jardinier">
            👩‍🌾
          </span>
          <div className="leading-tight">
            <p className="text-xs font-semibold uppercase tracking-wide">{t('multiGarden.gardenerBadgeTitle')}</p>
            <p className="text-sm text-green-50">
              {gardenerPulse
                ? t('multiGarden.gardenerPulse')
                : autoGardenerConfig
                  ? `${autoGardenerConfig.clicksPerTick || 1} clic(s) toutes les ${(autoGardenerConfig.intervalMs || 1000) / 1000}s`
                  : t('multiGarden.gardenerIdleFallback')}
            </p>
          </div>
        </div>
      )}
      <div 
        ref={canvasRef} 
        className="pixel-art w-full h-full max-w-full"
        style={{ 
          cursor: pendingSeedType ? 'crosshair' : selectedMode ? 'pointer' : 'default',
          touchAction: 'none'
        }}
      />

      <PendingSeedsOverlay
        pendingSeedsCount={!isAnimating ? pendingSeeds.length : 0}
        onValidate={handleValidatePendingSeeds}
      />

      <WaterNotification message={waterNotification} />

      {/* Toolbar d'icônes (DOM overlay, pas dans le snapshot) */}
      <MultiPlantToolbar
        selectedMode={selectedMode}
        waterLevel={waterLevel}
        onModeClick={handleModeClick}
        onZoomButton={handleZoomButton}
        onRequestPublish={onRequestPublish ? handlePublishRequest : undefined}
        showToolHint={showToolHint}
        hideToolHint={hideToolHint}
        handleToolTouchStart={handleToolTouchStart}
        handleToolTouchEnd={handleToolTouchEnd}
        hasPruner={ownedTools?.includes('pruning_shears')}
        waterCooldownText={waterLevel < 10 && waterCooldownMs > 0 ? formatDuration(waterCooldownMs) : null}
        hasPlants={hasPlants}
        canPublish={hasMaturePlant}
      />

      <OffscreenIndicators left={offscreenLeft} right={offscreenRight} />

      <ToolHintOverlay hint={activeToolHint} />
    </div>
  )
}

export default MultiPlantCanvas
